import { alpha, createTheme, darken, lighten } from '@mui/material';
import { PureLightTheme } from './PureLightTheme';

const darkThemeColors = {
  primary: '#F26F21',
  secondary: '#9AA8C6',
  success: '#3DD68C',
  warning: '#F4B740',
  error: '#ef4444',
  info: '#4F9DFF',
  black: '#000000',
  white: '#ffffff',
  primaryAlt: '#E2570A'
};

export const DarkTheme = createTheme({
  // Base off the existing light theme extensions to satisfy ThemeOptions augmentation
  colors: {
    ...PureLightTheme.colors,
    gradients: PureLightTheme.colors.gradients,
    shadows: {
      success: '0px 1px 4px rgba(61, 214, 140, 0.4), 0px 3px 12px 2px rgba(61, 214, 140, 0.4)',
      error: '0px 1px 4px rgba(239, 68, 68, 0.4), 0px 3px 12px 2px rgba(239, 68, 68, 0.4)',
      info: '0px 1px 4px rgba(79, 157, 255, 0.4), 0px 3px 12px 2px rgba(79, 157, 255, 0.4)',
      primary: '0px 1px 4px rgba(242, 111, 33, 0.4), 0px 3px 12px 2px rgba(242, 111, 33, 0.4)',
      warning: '0px 1px 4px rgba(244, 183, 64, 0.4), 0px 3px 12px 2px rgba(244, 183, 64, 0.4)'
    },
    alpha: {
      white: {
        5: alpha(darkThemeColors.white, 0.02),
        10: alpha(darkThemeColors.white, 0.1),
        30: alpha(darkThemeColors.white, 0.3),
        50: alpha(darkThemeColors.white, 0.5),
        70: alpha(darkThemeColors.white, 0.7),
        100: darkThemeColors.white
      },
      trueWhite: {
        5: alpha(darkThemeColors.white, 0.02),
        10: alpha(darkThemeColors.white, 0.1),
        30: alpha(darkThemeColors.white, 0.3),
        50: alpha(darkThemeColors.white, 0.5),
        70: alpha(darkThemeColors.white, 0.7),
        100: darkThemeColors.white
      },
      black: {
        5: alpha(darkThemeColors.black, 0.02),
        10: alpha(darkThemeColors.black, 0.1),
        30: alpha(darkThemeColors.black, 0.3),
        50: alpha(darkThemeColors.black, 0.5),
        70: alpha(darkThemeColors.black, 0.7),
        100: darkThemeColors.black
      }
    },
    secondary: {
      lighter: lighten(darkThemeColors.secondary, 0.85),
      light: lighten(darkThemeColors.secondary, 0.25),
      main: darkThemeColors.secondary,
      dark: darken(darkThemeColors.secondary, 0.2)
    },
    primary: {
      lighter: lighten(darkThemeColors.primary, 0.85),
      light: lighten(darkThemeColors.primary, 0.25),
      main: darkThemeColors.primary,
      dark: darken(darkThemeColors.primary, 0.2)
    },
    success: {
      lighter: lighten(darkThemeColors.success, 0.85),
      light: lighten(darkThemeColors.success, 0.25),
      main: darkThemeColors.success,
      dark: darken(darkThemeColors.success, 0.2)
    },
    warning: {
      lighter: lighten(darkThemeColors.warning, 0.85),
      light: lighten(darkThemeColors.warning, 0.25),
      main: darkThemeColors.warning,
      dark: darken(darkThemeColors.warning, 0.2)
    },
    error: {
      lighter: lighten(darkThemeColors.error, 0.85),
      light: lighten(darkThemeColors.error, 0.25),
      main: darkThemeColors.error,
      dark: darken(darkThemeColors.error, 0.2)
    },
    info: {
      lighter: lighten(darkThemeColors.info, 0.85),
      light: lighten(darkThemeColors.info, 0.25),
      main: darkThemeColors.info,
      dark: darken(darkThemeColors.info, 0.2)
    }
  } as any,
  general: PureLightTheme.general as any,
  sidebar: {
    background: '#0A1322',
    textColor: '#9AA8C6',
    dividerBg: 'rgba(255,255,255,0.06)',
    width: '290px',
    menuItemColor: '#9AA8C6',
    menuItemColorActive: '#ffffff',
    menuItemBg: '#0A1322',
    menuItemBgActive: 'rgba(242,111,33,0.13)',
    menuItemIconColor: '#9AA8C6',
    menuItemIconColorActive: '#E45A0D',
    menuItemHeadingColor: '#5C6D8A',
    scrollbarColor: '#F26F21',
    boxShadow: 'none'
  } as any,
  header: {
    height: '80px',
    background: '#060D1A',
    textColor: '#9AA8C6',
    boxShadow: '0 1px 0 rgba(255,255,255,0.05)'
  } as any,
  spacing: PureLightTheme.spacing,
  breakpoints: PureLightTheme.breakpoints,
  components: {
    ...PureLightTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#2A3A55 #0A1322',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#2A3A55',
            minHeight: 24,
            border: '2px solid #0A1322'
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            borderRadius: 8,
            backgroundColor: '#0A1322'
          }
        },
        /* Global mobile helpers */
        '.mobile-scroll-x': {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        },
        '.MuiTableContainer-root': {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        },
        '@media (max-width: 960px)': {
          '.MuiButton-root': {
            minHeight: 40
          },
          '.MuiIconButton-root': {
            padding: 8
          },
          '.MuiContainer-root': {
            paddingLeft: 12,
            paddingRight: 12
          }
        },
        '@media (max-width: 600px)': {
          '.MuiTableCell-root': {
            padding: '8px 10px',
            fontSize: 13
          },
          '.MuiTableCell-head': {
            fontSize: 12
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    }
  },
  shape: PureLightTheme.shape,
  typography: PureLightTheme.typography,
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
  ],
  palette: {
    mode: 'dark',
    common: {
      black: '#000000',
      white: '#ffffff'
    },
    primary: {
      lighter: lighten(darkThemeColors.primary, 0.85),
      light: lighten(darkThemeColors.primary, 0.25),
      main: darkThemeColors.primary,
      dark: darken(darkThemeColors.primary, 0.2),
      contrastText: '#ffffff'
    },
    secondary: {
      lighter: lighten(darkThemeColors.secondary, 0.85),
      light: lighten(darkThemeColors.secondary, 0.25),
      main: darkThemeColors.secondary,
      dark: darken(darkThemeColors.secondary, 0.2),
      contrastText: '#ffffff'
    },
    success: {
      lighter: lighten(darkThemeColors.success, 0.85),
      light: lighten(darkThemeColors.success, 0.25),
      main: darkThemeColors.success,
      dark: darken(darkThemeColors.success, 0.2),
      contrastText: '#ffffff'
    },
    warning: {
      lighter: lighten(darkThemeColors.warning, 0.85),
      light: lighten(darkThemeColors.warning, 0.25),
      main: darkThemeColors.warning,
      dark: darken(darkThemeColors.warning, 0.2),
      contrastText: '#ffffff'
    },
    error: {
      lighter: lighten(darkThemeColors.error, 0.85),
      light: lighten(darkThemeColors.error, 0.25),
      main: darkThemeColors.error,
      dark: darken(darkThemeColors.error, 0.2),
      contrastText: '#ffffff'
    },
    info: {
      lighter: lighten(darkThemeColors.info, 0.85),
      light: lighten(darkThemeColors.info, 0.25),
      main: darkThemeColors.info,
      dark: darken(darkThemeColors.info, 0.2),
      contrastText: '#ffffff'
    },
    background: {
      default: '#060D1A',
      paper: '#0F1B2D'
    },
    text: {
      primary: '#e2e8f0',
      secondary: '#9AA8C6',
      disabled: '#4A5568'
    },
    action: {
      active: '#ffffff',
      hover: alpha('#ffffff', 0.08),
      hoverOpacity: 0.08,
      selected: alpha('#ffffff', 0.16),
      selectedOpacity: 0.16,
      disabled: alpha('#ffffff', 0.3),
      disabledBackground: alpha('#ffffff', 0.12),
      disabledOpacity: 0.38,
      focus: alpha('#ffffff', 0.12),
      focusOpacity: 0.12,
      activatedOpacity: 0.24
    },
    divider: alpha('#ffffff', 0.12),
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#f5f5f5',
      A200: '#eeeeee',
      A400: '#bdbdbd',
      A700: '#616161'
    }
  }
} as any);


